import { lazy, type ReactElement } from "react";
import { SuspenseWrapper } from "@/shared/components/feedback/SuspenseWrapper";

// Lazy-loaded pages
const DashboardPage   = lazy(() => import("@/pages/DashboardPage"));
const LocationsPage   = lazy(() => import("@/pages/LocationsPage"));
const AssetsPage      = lazy(() => import("@/pages/AssetsPage"));
const WorkOrdersPage  = lazy(() => import("@/pages/WorkOrdersPage"));
const PmSchedulesPage = lazy(() => import("@/pages/PmSchedulesPage"));
const ChecklistsPage  = lazy(() => import("@/pages/ChecklistsPage"));
const TemplatesPage   = lazy(() => import("@/pages/TemplatesPage"));
const InspectionsPage = lazy(() => import("@/pages/InspectionsPage"));
const ActionsPage     = lazy(() => import("@/pages/ActionsPage"));
const InspectionSchedulesPage = lazy(() => import("@/pages/InspectionSchedulesPage"));
const ReportsPage     = lazy(() => import("@/pages/ReportsPage"));
const AnalyticsPage   = lazy(() => import("@/pages/AnalyticsPage"));
const UsersPage       = lazy(() => import("@/pages/UsersPage"));
const SettingsPage    = lazy(() => import("@/pages/SettingsPage"));
const LaborTrackingPage = lazy(() => import("@/pages/LaborTrackingPage"));
const AuditLogPage    = lazy(() => import("@/pages/AuditLogPage"));
const CustomFieldsPage = lazy(() => import("@/pages/CustomFieldsPage"));
const InventoryPage   = lazy(() => import("@/pages/InventoryPage"));
const VendorsPage     = lazy(() => import("@/pages/VendorsPage"));
const IncidentsPage   = lazy(() => import("@/pages/IncidentsPage"));
const MeterReadingsPage = lazy(() => import("@/pages/MeterReadingsPage"));
const EscalationRulesPage = lazy(() => import("@/pages/EscalationRulesPage"));
const IntegrationsPage = lazy(() => import("@/pages/IntegrationsPage"));
const TeamsPage       = lazy(() => import("@/pages/TeamsPage"));
const AssetDetailPage = lazy(() => import("@/pages/AssetDetailPage"));
const LocationDetailPage = lazy(() => import("@/pages/LocationDetailPage"));
const WorkOrderDetailPage = lazy(() => import("@/pages/WorkOrderDetailPage"));
const InspectionDetailPage = lazy(() => import("@/pages/InspectionDetailPage"));
const TemplateDetailPage = lazy(() => import("@/pages/TemplateDetailPage"));
const TemplateEditorPage = lazy(() => import("@/pages/TemplateEditorPage"));
const MeterReadingDetailPage = lazy(() => import("@/pages/MeterReadingDetailPage"));
const EscalationRuleDetailPage = lazy(() => import("@/pages/EscalationRuleDetailPage"));
const VendorDetailPage = lazy(() => import("@/pages/VendorDetailPage"));
const PmScheduleDetailPage = lazy(() => import("@/pages/PmScheduleDetailPage"));
const IncidentDetailPage = lazy(() => import("@/pages/IncidentDetailPage"));
const ChecklistDetailPage = lazy(() => import("@/pages/ChecklistDetailPage"));
const ActionDetailPage = lazy(() => import("@/pages/ActionDetailPage"));
const UserDetailPage  = lazy(() => import("@/pages/UserDetailPage"));
const AnalyticsWorkOrdersPage = lazy(() => import("@/pages/AnalyticsWorkOrdersPage"));
const AnalyticsAssetsPage = lazy(() => import("@/pages/AnalyticsAssetsPage"));
const AnalyticsPreventivePage = lazy(() => import("@/pages/AnalyticsPreventivePage"));
const AnalyticsTeamPage = lazy(() => import("@/pages/AnalyticsTeamPage"));
const AnalyticsSafetyPage = lazy(() => import("@/pages/AnalyticsSafetyPage"));
const NotFoundPage    = lazy(() => import("@/pages/NotFoundPage"));

interface RouteConfig {
  path: string;
  element: ReactElement;
}

// Helper to wrap lazy components in SuspenseWrapper
const withSuspense = (Component: React.LazyExoticComponent<React.ComponentType<unknown>>) => (
  <SuspenseWrapper>
    <Component />
  </SuspenseWrapper>
);

// ─────────────────────────────────────────────────────────────────────────────
// Route definitions — maintenance + inspection modules
// ─────────────────────────────────────────────────────────────────────────────

export const appRoutes: RouteConfig[] = [
  { path: "/",            element: withSuspense(DashboardPage) },
  { path: "/dashboard",   element: withSuspense(DashboardPage) },
  { path: "/locations/:id", element: withSuspense(LocationDetailPage) },
  { path: "/locations",   element: withSuspense(LocationsPage) },
  { path: "/assets/:id",  element: withSuspense(AssetDetailPage) },
  { path: "/assets",      element: withSuspense(AssetsPage) },
  { path: "/work-orders/:id", element: withSuspense(WorkOrderDetailPage) },
  { path: "/work-orders", element: withSuspense(WorkOrdersPage) },
  { path: "/pm-schedules/:id", element: withSuspense(PmScheduleDetailPage) },
  { path: "/pm-schedules",element: withSuspense(PmSchedulesPage) },
  { path: "/checklists/:id", element: withSuspense(ChecklistDetailPage) },
  { path: "/checklists",  element: withSuspense(ChecklistsPage) },
  { path: "/templates/new",     element: withSuspense(TemplateEditorPage) },
  { path: "/templates/:id/edit", element: withSuspense(TemplateEditorPage) },
  { path: "/templates/:id", element: withSuspense(TemplateDetailPage) },
  { path: "/templates",   element: withSuspense(TemplatesPage) },
  { path: "/inspections/:id",       element: withSuspense(InspectionDetailPage) },
  { path: "/inspections",           element: withSuspense(InspectionsPage) },
  { path: "/actions/:id",           element: withSuspense(ActionDetailPage) },
  { path: "/actions",               element: withSuspense(ActionsPage) },
  { path: "/inspection-schedules",  element: withSuspense(InspectionSchedulesPage) },
  { path: "/reports",               element: withSuspense(ReportsPage) },
  { path: "/analytics/work-orders", element: withSuspense(AnalyticsWorkOrdersPage) },
  { path: "/analytics/assets",      element: withSuspense(AnalyticsAssetsPage) },
  { path: "/analytics/preventive",  element: withSuspense(AnalyticsPreventivePage) },
  { path: "/analytics/team",        element: withSuspense(AnalyticsTeamPage) },
  { path: "/analytics/safety",      element: withSuspense(AnalyticsSafetyPage) },
  { path: "/analytics",             element: withSuspense(AnalyticsPage) },
  { path: "/labor-tracking",  element: withSuspense(LaborTrackingPage) },
  { path: "/inventory",      element: withSuspense(InventoryPage) },
  { path: "/vendors/:id",    element: withSuspense(VendorDetailPage) },
  { path: "/vendors",        element: withSuspense(VendorsPage) },
  { path: "/incidents/:id",  element: withSuspense(IncidentDetailPage) },
  { path: "/incidents",      element: withSuspense(IncidentsPage) },
  { path: "/meter-readings/:id", element: withSuspense(MeterReadingDetailPage) },
  { path: "/meter-readings", element: withSuspense(MeterReadingsPage) },
  { path: "/escalation-rules/:id", element: withSuspense(EscalationRuleDetailPage) },
  { path: "/escalation-rules", element: withSuspense(EscalationRulesPage) },
  { path: "/users/:id",      element: withSuspense(UserDetailPage) },
  { path: "/teams",          element: withSuspense(TeamsPage) },
  { path: "/users",          element: withSuspense(UsersPage) },
  { path: "/audit-log",      element: withSuspense(AuditLogPage) },
  { path: "/custom-fields",  element: withSuspense(CustomFieldsPage) },
  { path: "/integrations",   element: withSuspense(IntegrationsPage) },
  { path: "/settings",       element: withSuspense(SettingsPage) },
  { path: "*",               element: withSuspense(NotFoundPage) },
];
